import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
//import { Excalidraw, MainMenu } from '@excalidraw/excalidraw';
//import '@excalidraw/excalidraw/index.css';
import './BizWhiteboard.scss';
import { useJoinWhiteboard, useCreateSnapshot, useSaveWhiteboardFile } from '../../Hooks/useWhiteboardHooks';
import { useSignalRContext } from '../../Contexts/signalrContext';

import { useGlobalState } from '../../Contexts/globalStateContext';
import { ObjectType } from '../../Types/base';
import BizIconButton from '../Mini/BizIconButton';
import { whiteboardReducer, WhiteboardState } from './reducer';
import { useWhiteboardYjs } from './Hooks/useWhiteboardYjs';
import { useWhiteboardSignalR } from './Hooks/useWhiteboardSignalR';
import { useWhiteboardFiles } from './Hooks/useWhiteboardFiles';
import { useWhiteboardSnapshot } from './Hooks/useWhiteboardSnapshot';
import { USER_ACTIVITY_TIMEOUT_MS } from './constants';
import { versionHash, yToExcalidrawElements } from './utils';
import { useExcalidraw } from './Hooks/useExcalidraw';
import type { Excalidraw as ExcalidrawType, MainMenu as MainMenuType } from '@excalidraw/excalidraw';

type BizWhiteboardProps = {
	objectType: ObjectType;
	objectId: number;
	name?: string;
	className?: string;
	height?: string;
	allowEdit?: boolean;
};

type WhiteboardProps = BizWhiteboardProps & {
	fullscreen: boolean;
	Excalidraw: typeof ExcalidrawType;
	MainMenu: typeof MainMenuType;
};

const BizWhiteboard = ({
	objectType,
	objectId,
	name = 'Whiteboard',
	className = '',
	height = '500px',
	allowEdit = true,
}: BizWhiteboardProps) => {
	const { Excalidraw, MainMenu } = useExcalidraw();
	const { showModal } = useGlobalState();
	const [maximized, setMaximized] = useState(false);

	if (Excalidraw === null || MainMenu === null) {
		return (
			<div role='status' aria-live='polite'>
				Loading Whiteboard library...
			</div>
		);
	}

	const onMaximize = () => {
		setMaximized(true);
		showModal(
			<Whiteboard
				objectType={objectType}
				objectId={objectId}
				name={name}
				className={className}
				height={height}
				allowEdit={allowEdit}
				fullscreen={true}
				Excalidraw={Excalidraw}
				MainMenu={MainMenu}
			/>,
			'Whiteboard',
			() => {
				setMaximized(false);
			}
		);
	};

	return (
		<div className='biz-whiteboard-size-toggle-wrapper'>
			<div className='biz-whiteboard-maximize-button-wrapper'>
				<BizIconButton icon='MAXIMIZE' title='Maximize' onClick={onMaximize} />
			</div>
			{!maximized && (
				<Whiteboard
					objectType={objectType}
					objectId={objectId}
					name={name}
					className={className}
					height={height}
					allowEdit={allowEdit}
					fullscreen={false}
					Excalidraw={Excalidraw}
					MainMenu={MainMenu}
				/>
			)}
		</div>
	);
};

const initialWhiteboardState: WhiteboardState = {
	isLoading: true,
	collaboratorCursors: new Map(),
	isHydrating: false,
	lastVersionHash: '',
	recentRemoteHashes: [],
	hasJoinedSignalR: false,
	lastPresenceUpdate: 0,
};

const Whiteboard = ({
	objectType,
	objectId,
	className = '',
	height = '500px',
	allowEdit = true,
	fullscreen = false,
	Excalidraw,
	MainMenu,
}: WhiteboardProps) => {
	const {
		state: { user },
	} = useGlobalState();

	const [state, dispatch] = useReducer(whiteboardReducer, initialWhiteboardState);
	const lastActivityTimeRef = useRef(0);
	const userIsActiveRef = useRef(false);

	userIsActiveRef.current =
		lastActivityTimeRef.current > 0 && lastActivityTimeRef.current > Date.now() - USER_ACTIVITY_TIMEOUT_MS;

	//console.log('User is active:', userIsActiveRef.current);

	const {
		data: joinResponse,
		statusState,
		resetQuery: resetQueryJoinWhiteboard,
		removeQuery: removeQueryJoinWhiteboard,
	} = useJoinWhiteboard(objectType, objectId, state.hasJoinedSignalR);

	const { createSnapshot } = useCreateSnapshot();
	const { saveWhiteboardFile, statusState: saveFileStatus } = useSaveWhiteboardFile();
	const { sendMessage } = useSignalRContext();

	const yjsHook = useWhiteboardYjs(
		joinResponse?.whiteboard?.id,
		user,
		objectType,
		objectId,
		sendMessage,
		state.isHydrating,
		dispatch,
		userIsActiveRef
	);

	const snapshotHook = useWhiteboardSnapshot(
		joinResponse?.whiteboard?.id,
		lastActivityTimeRef,
		yjsHook.yjsDocRef,
		yjsHook.yElementsRef,
		createSnapshot,
		dispatch
	);

	const fileHook = useWhiteboardFiles(
		joinResponse?.whiteboard?.id,
		yjsHook.excalidrawRef,
		saveWhiteboardFile,
		sendMessage,
		objectType,
		objectId
	);

	// Extract needed data
	const whiteboardData = joinResponse?.whiteboard;
	const latestSnapshot = joinResponse?.latestSnapshot;
	const pendingUpdates = joinResponse?.pendingUpdates || [];
	const cacheAvailable = joinResponse?.cacheAvailable || false;
	const whiteboardId = whiteboardData?.id;

	const signalRHook = useWhiteboardSignalR(
		objectType,
		objectId,
		user,
		dispatch,
		fileHook.addFiles,
		yjsHook.applyYjsUpdate,
		resetQueryJoinWhiteboard
	);

	// Cleanup on mount/unmount
	useEffect(() => {
		console.log('Whiteboard MOUNT');
		return () => {
			removeQueryJoinWhiteboard();
			console.log('Whiteboard UNMOUNT');
		};
	}, []); // Empty dependencies to prevent re-running

	// File saving success notification
	useEffect(() => {
		fileHook.notifyFileSaved(saveFileStatus, whiteboardId);
	}, [saveFileStatus, whiteboardId, fileHook.notifyFileSaved]);

	// Clean up stale cursors
	useEffect(() => {
		const interval = setInterval(() => {
			dispatch({ type: 'CLEAN_STALE_CURSORS' });
		}, 1000);

		return () => clearInterval(interval);
	}, []);

	// Load initial whiteboard data
	useEffect(() => {
		if (
			statusState === 'SUCCESS' &&
			yjsHook.excalidrawRef.current &&
			yjsHook.yjsDocRef.current &&
			yjsHook.yElementsRef.current &&
			joinResponse &&
			state.hasJoinedSignalR
		) {
			dispatch({ type: 'SET_HYDRATING', payload: true });

			console.log('Loading snapshot after join');
			//const els = yjsHook.yElementsRef.current!.toArray();
			//const els = yToExcalidrawElements(yjsHook.yElementsRef.current);
			//els.forEach(Object.freeze);
			//yjsHook.excalidrawRef.current.updateScene({ elements: els });

			dispatch({ type: 'SET_LOADING', payload: false });

			if (joinResponse.whiteboard?.files && joinResponse.whiteboard.files.length > 0) {
				console.log('Loading initial files:', joinResponse.whiteboard.files.length);
				fileHook.addFiles(joinResponse.whiteboard.files);
			}

			if (latestSnapshot?.yjsStateUpdate) {
				try {
					yjsHook.applySnapshot(latestSnapshot.yjsStateUpdate);
					console.log(
						'Loaded snapshot with version hash:',
						versionHash(yToExcalidrawElements(yjsHook.yElementsRef.current!))
					);

					if (cacheAvailable && pendingUpdates.length > 0) {
						yjsHook.applyPendingUpdates(pendingUpdates);
					}
				} catch (error) {
					console.error('Error parsing snapshot data:', error);
				}
			} else {
				console.log('No snapshot found');
			}

			yjsHook.purgeDeletedElements();
			yjsHook.paintFromY(false, true);

			dispatch({ type: 'SET_HYDRATING', payload: false });
			console.log('Whiteboard data loaded');
		} else if (statusState === 'ERROR') {
			dispatch({ type: 'SET_LOADING', payload: false });
		}
	}, [
		statusState,
		joinResponse,
		state.hasJoinedSignalR,
		fileHook.addFiles,
		latestSnapshot?.yjsStateUpdate,
		cacheAvailable,
		pendingUpdates.length,
		yjsHook.applySnapshot,
		yjsHook.applyPendingUpdates,
		yjsHook.purgeDeletedElements,
		yjsHook.paintFromY,
	]);

	// Handle Excalidraw onChange
	const handleChange = useCallback(
		//eslint-disable-next-line @typescript-eslint/no-explicit-any
		(elements: readonly any[], appState: any) => {
			yjsHook.handleChange(
				elements,
				appState,
				state.lastVersionHash,
				state.recentRemoteHashes,
				state.hasJoinedSignalR,
				allowEdit,
				snapshotHook.trackActivity
			);
		},
		[
			yjsHook.handleChange,
			state.lastVersionHash,
			state.recentRemoteHashes,
			state.hasJoinedSignalR,
			allowEdit,
			snapshotHook.trackActivity,
		]
	);

	// Mouse movement tracking for presence updates
	useEffect(() => {
		if (!allowEdit || !yjsHook.excalidrawRef.current) return;

		const convertToSceneCoords = (x: number, y: number) => {
			const appState = yjsHook.excalidrawRef.current!.getAppState();
			const zoomValue = appState.zoom.value;
			const scrollX = appState.scrollX;
			const scrollY = appState.scrollY;
			return {
				sceneX: x / zoomValue - scrollX,
				sceneY: y / zoomValue - scrollY,
			};
		};

		const handleMouseMove = (event: Event) => {
			const mouseEvent = event as unknown as MouseEvent;
			const rect = (mouseEvent.target as Element)?.closest('.excalidraw')?.getBoundingClientRect();
			if (rect) {
				const sceneCoords = convertToSceneCoords(mouseEvent.clientX - rect.left, mouseEvent.clientY - rect.top);
				signalRHook.sendPresenceUpdate(sceneCoords.sceneX, sceneCoords.sceneY, state.lastPresenceUpdate);
			}
		};

		const handleKeyDown = (event: Event) => {
			const keyboardEvent = event as unknown as KeyboardEvent;
			if ((keyboardEvent.ctrlKey || keyboardEvent.metaKey) && !keyboardEvent.shiftKey && keyboardEvent.key === 'z') {
				console.log('Undo action triggered');
				keyboardEvent.stopPropagation();
				yjsHook.undo();
				snapshotHook.trackActivity(true);
			} else if ((keyboardEvent.ctrlKey || keyboardEvent.metaKey) && keyboardEvent.shiftKey && keyboardEvent.key === 'z') {
				console.log('Redo action triggered');
				keyboardEvent.stopPropagation();
				// Redo not implemented yet
			}
		};

		const excalidrawElement = document.querySelector('.excalidraw');
		if (excalidrawElement) {
			excalidrawElement.addEventListener('mousemove', handleMouseMove);
			excalidrawElement.addEventListener('keydown', handleKeyDown);

			return () => {
				excalidrawElement.removeEventListener('mousemove', handleMouseMove);
				excalidrawElement.removeEventListener('keydown', handleKeyDown);
			};
		}
	}, [
		allowEdit,
		yjsHook.excalidrawRef,
		yjsHook.undo,
		signalRHook.sendPresenceUpdate,
		state.lastPresenceUpdate,
		snapshotHook.trackActivity,
	]);

	function mapSceneCoordinatesToViewport(sceneX: number, sceneY: number) {
		const appState = yjsHook.excalidrawRef?.current?.getAppState();
		if (!appState) {
			return { x: sceneX, y: sceneY };
		}
		const x = (sceneX + appState.scrollX) * appState.zoom.value;
		const y = (sceneY + appState.scrollY) * appState.zoom.value;
		return { x, y };
	}

	if (whiteboardData === null && statusState === 'SUCCESS') {
		return (
			<div className={`biz-whiteboard ${className}`} style={{ height }}>
				Whiteboard not found.
			</div>
		);
	}

	return (
		<div className={`biz-whiteboard ${className} ${fullscreen ? 'maximized' : ''}`} style={{ height }}>
			{state.isLoading && (
				<div className='biz-whiteboard-loading'>
					<div>Loading whiteboard...</div>
				</div>
			)}
			<Excalidraw
				excalidrawAPI={(api) => {
					yjsHook.excalidrawRef.current = api;
					// Don't trigger any side effects here - just set the ref
				}}
				onChange={handleChange}
				viewModeEnabled={!allowEdit}
				UIOptions={{
					canvasActions: {
						loadScene: false,
						saveToActiveFile: false,
						export: false,
						toggleTheme: true,
					},
					tools: {
						image: true,
					},
				}}
			>
				<MainMenu />
			</Excalidraw>
			{/* Render collaborator cursors */}
			{Array.from(state.collaboratorCursors.values()).map((cursor) => {
				const viewportPos = mapSceneCoordinatesToViewport(cursor.x, cursor.y);
				return (
					<div
						key={cursor.userId}
						className='biz-whiteboard-cursor'
						style={{
							position: 'absolute',
							left: viewportPos.x,
							top: viewportPos.y,
							pointerEvents: 'none',
							zIndex: 1000,
						}}
					>
						<div className='cursor-dot'></div>
						{cursor.userName && <div className='cursor-label'>{cursor.userName}</div>}
					</div>
				);
			})}
			{statusState === 'LOADING' && <div className='biz-whiteboard-save-indicator'>Loading...</div>}
		</div>
	);
};

export default BizWhiteboard;
